/**
 * Calendar-parity client calls for routes that don't exist on `main` yet
 * (they land in Phase B of this build — see the PR description). Kept in
 * this separate file rather than client/src/lib/api.ts because another
 * concurrent agent owns api.ts edits in Phase A; once Phase B adds the
 * routes below, these thin wrappers can be folded into api.ts (or left
 * standalone — either is fine).
 *
 * All calls go through fetchWithAuth (imported from api.ts, NOT modifying
 * it) so auth headers / error handling match every other client call.
 */
import { fetchWithAuth } from "./api";
import type { RecurrenceChoice } from "./calendarTypes";

export interface CalendarEventPatch {
  subject?: string;
  date?: string; // "YYYY-MM-DD"
  startTime?: string; // "HH:mm"
  endTime?: string; // "HH:mm"
  isAllDay?: boolean;
  showAs?: "free" | "busy" | "tentative" | "oof" | "workingElsewhere";
  notes?: string;
  attendees?: Array<{ email: string; name?: string }>;
  recurrence?: RecurrenceChoice | null;
}

export interface CalendarEventCreatePayload {
  subject: string;
  date: string;
  startTime?: string;
  endTime?: string;
  isAllDay: boolean;
  showAs: "free" | "busy" | "tentative" | "oof" | "workingElsewhere";
  notes?: string;
  attendees?: Array<{ email: string; name?: string }>;
  recurrence?: RecurrenceChoice | null;
}

/**
 * Create an event on the AUTHENTICATED USER'S OWN calendar only — the
 * server never accepts a targetEmail for this route (Session-163 decision,
 * closed for real in Phase B). Superset of the plain Block Time create:
 * optional attendees (native Graph invite) and recurrence.
 */
export async function createCalendarEventFull(payload: CalendarEventCreatePayload): Promise<any> {
  return fetchWithAuth<any>("/api/calendar/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Edit a CentCom-authored event on the caller's OWN calendar. Only
 * reachable in the UI where Graph's `isOrganizer` is true for the event
 * (see EditEventModal) — events where the user is merely an attendee get
 * "Remove from my calendar" instead.
 */
export async function updateCalendarEventFull(eventId: string, patch: CalendarEventPatch): Promise<any> {
  return fetchWithAuth<any>(`/api/calendar/events/${encodeURIComponent(eventId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

/** Delete a CentCom-authored event on the caller's OWN calendar (Graph auto-notifies attendees). */
export async function deleteCalendarEventFull(eventId: string): Promise<void> {
  await fetchWithAuth(`/api/calendar/events/${encodeURIComponent(eventId)}`, { method: "DELETE" });
}

/** Delete a single occurrence of a recurring own-calendar event (Graph occurrence id, not the series master). */
export async function deleteCalendarEventOccurrence(occurrenceEventId: string): Promise<void> {
  await fetchWithAuth(`/api/calendar/events/${encodeURIComponent(occurrenceEventId)}`, { method: "DELETE" });
}

export interface MeetingUpdatePatch {
  topic?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  /**
   * The `meetings` table has no durable attendee list (no migration in this
   * pass), so the caller must resend the recipient list it already has
   * (from the meeting record / UI state) whenever topic/time change, so the
   * server can rebuild + resend the ICS invite to the right people.
   */
  attendee_emails?: string[];
}

/** PATCH a CentCom Zoom meeting: updates Zoom + resends an updated (SEQUENCE++) ICS invite. */
export async function updateMeetingFull(meetingId: string, patch: MeetingUpdatePatch): Promise<any> {
  return fetchWithAuth<any>(`/api/meetings/${meetingId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

/** DELETE (cancel) a CentCom Zoom meeting: deletes the Zoom meeting + sends a METHOD:CANCEL ICS. */
export async function cancelMeetingFull(meetingId: string, attendeeEmails: string[]): Promise<void> {
  await fetchWithAuth(`/api/meetings/${meetingId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attendee_emails: attendeeEmails }),
  });
}
